import { Controller } from 'stimulus'
import Siema from 'vendor/siema'

/**
 * Manages js for a slideshow (StimulusJS controller)
 *
 * @class LoanOptionsController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [ 'slideContainer' ]

  /**
   * @function Controllers.SlideshowController.connect
   * @memberof Controllers
   * @description Creates a callback to cancel the auto slideshow. Meant to fire
   * when a user interacts with the slides.
   *
   * @return {undefined} manipulates DOM
   *
   */
  initialize() {
    this.slideshowCancelCallback = () => {
      clearInterval(this.timerInterval)
    }
  }

  /**
   * @function Controllers.SlideshowController.connect
   * @memberof Controllers
   * @description Creates a new Siema instance, adds overriding listeners, and
   * starts the automatic slideshow
   *
   * @return {undefined} manipulates DOM
   *
   */
  connect() {
    const draggable = this.data.get('draggable') === 'true'

    this.slideshow = new Siema({
      selector:  this.slideContainerTarget,
      duration:  parseInt(this.data.get('transition-length')),
      loop:      true,
      draggable: draggable,
    })

    this._maybeAddListeners()
    this._startTimer(this.data.get('delay'))
  }

  /**
   * @function Controllers.SlideshowController.next
   * @memberof Controllers
   * @description Cancels the show and calls for the next slide
   * autoplay the show
   *
   * @param {Event} event - User interaction with slide or controls
   *
   * @return {undefined} Manipulates DOM
   *
   */
  next(event) {
    event.preventDefault()
    this.slideshowCancelCallback()
    this.slideshow.next()
  }

  /**
   * @function Controllers.SlideshowController.previous
   * @memberof Controllers
   * @description Cancels the show and calls for the previous slide
   * autoplay the show
   *
   * @param {Event} event - User interaction with slide or controls
   *
   * @return {undefined} Manipulates DOM
   *
   */
  previous(event) {
    event.preventDefault()
    this.slideshowCancelCallback()
    this.slideshow.prev()
  }


  // Private


  /**
   * @function Controllers.SlideshowController._addListeners
   * @memberof Controllers
   * @description Adds listeners to capture user interaction with the slideshow
   * to cancel the autoplay.
   *
   * @return {undefined} manipulates DOM
   *
   */
  _maybeAddListeners() {
    if (this.data.get('cancelable') === 'true') {
      this.slideshow.selector.addEventListener('touchstart', this.slideshowCancelCallback)
      this.slideshow.selector.addEventListener('mousedown', this.slideshowCancelCallback)
      this.slideshow.selector.addEventListener('click', this.slideshowCancelCallback)
    }
  }

  /**
   * @function Controllers.SlideshowController._startTimer
   * @memberof Controllers
   * @description Sets an interval to call next on the Siema instance and
   * autoplay the show
   *
   * @param {String} delay - The amount of delay for pauses between slide change.
   *
   * @return {undefined} manipulates DOM
   *
   */
  _startTimer(delay) {
    this.timerInterval = setInterval(() => {
      this.slideshow.next()
    }, delay)
  }
}
